import React, { useEffect, useState } from 'react';
import { getJobOpeningDetails } from '../../../../../utils/api_service/JobOpenings';
import { Applicant, PieItem } from '../../../../../utils/types/JobOpenings';
import { BarChart } from '../../ui/Charts/BarChart';
import { NumberInfo } from '../../ui/Charts/NumberInfo';

type OverviewProps = {
    candidates: Applicant[],
    loading: boolean,
    jobOpeningId: string
};

export const Overview = ({
  candidates,
  loading,
  jobOpeningId,
}: OverviewProps) => {

  const [deadline, setDeadline] = useState<number>(0);

  useEffect(() => {
    getDetailsDate();
  }, []);

  const getDetailsDate = async () => {
    try {
      const res = await getJobOpeningDetails(jobOpeningId);
      if (res.ok) {
        const data = await res.json();
        const deadline = data.jobRequisition.deadline;

        const today = new Date();
        today.setHours(0, 0, 0, 0); // Normalize to start of day

        const deadlineDate = new Date(deadline * 1000); // Convert Unix timestamp to ms
        deadlineDate.setHours(0, 0, 0, 0); // Normalize to start of day

        const msPerDay = 1000 * 60 * 60 * 24;
        const daysRemaining = Math.max(
          0,
          Math.floor((deadlineDate.getTime() - today.getTime()) / msPerDay),
        );

        setDeadline(daysRemaining);
      }
    } catch {
      setDeadline(0);
    }
  };

  const stats = [
    { label: 'Applicants', value: candidates.length },
    { label: 'Days to deadline', value: deadline },
  ];

  const genderPieData = (applicants: Applicant[]): PieItem[] => {
    const genderGroups: Record<string, number> = {
      Male: 0,
      Female: 0,
      'Non-binary': 0,
      Other: 0,
    };

    const genderColors: Record<string, string> = {
      Male: 'bg-green-100 text-green-900',
      Female: 'bg-yellow-100 text-yellow-900',
      'Non-binary': 'bg-rose-100 text-rose-900',
      Other: 'bg-blue-100 text-blue-900',
    };

    applicants.forEach((applicant) => {
      if (applicant.gender === 'Other') {
        genderGroups['Other']++;
      } else if (applicant.gender === 'Male') {
        genderGroups['Male']++;
      } else if (applicant.gender === 'Female') {
        genderGroups['Female']++;
      } else if (applicant.gender === 'Non-binary') {
        genderGroups['Non-binary']++;
      }
    });

    return Object.entries(genderGroups).map(([name, value]) => ({
      name: name === 'Non-binary' ? 'Non Binary' : name,
      value,
      color: genderColors[name],
    }));
  };

  const genderStats = genderPieData(candidates);

  const generateAgePieData = (applicants: Applicant[]): PieItem[] => {
    const ageGroups: Record<string, number> = {
      '18-25': 0,
      '25-35': 0,
      '35-50': 0,
      '50-60': 0,
      '60+': 0,
      'Unknown': 0,
    };

    const groupColors: Record<string, string> = {
      '18-25': 'bg-green-500',
      '25-35': 'bg-blue-500',
      '35-50': 'bg-rose-500',
      '50-60': 'bg-stone-500',
      '60+': 'bg-yellow-300',
      'Unknown': 'bg-gray-700',
    };

    applicants.forEach((applicant) => {
      if (applicant.age === null) {
        ageGroups['Unknown']++;
      } else if (applicant.age >= 18 && applicant.age <= 25) {
        ageGroups['18-25']++;
      } else if (applicant.age > 25 && applicant.age <= 35) {
        ageGroups['25-35']++;
      } else if (applicant.age > 35 && applicant.age <= 50) {
        ageGroups['35-50']++;
      } else if (applicant.age > 50 && applicant.age <= 60) {
        ageGroups['50-60']++;
      } else if (applicant.age > 60) {
        ageGroups['60+']++;
      }
    });

    return Object.entries(ageGroups).map(([name, value]) => ({
      name,
      value,
      color: groupColors[name],
    }));
  };

  const ageRange = generateAgePieData(candidates);

  const categorizeExperience = (data: Applicant[]): number[] => {
    let buckets = [0, 0, 0, 0, 0, 0, 0];

    data.forEach((item) => {
      const years = item.value[1];

      if (years >= 0 && years < 1) {
        buckets[0]++;
      } else if (years >= 1 && years <= 2) {
        buckets[1]++;
      } else if (years >= 3 && years <= 5) {
        buckets[2]++;
      } else if (years >= 6 && years <= 10) {
        buckets[3]++;
      } else if (years >= 11 && years <= 15) {
        buckets[4]++;
      } else if (years >= 16 && years <= 20) {
        buckets[5]++;
      } else if (years > 20) {
        buckets[6]++;
      }
    });

    return buckets;
  };

  const yAxisValues = categorizeExperience(candidates);

  return (
    <div className='grid grid-cols-1 gap-4 py-4 sm:grid-cols-2 xl:grid-cols-4'>
      { /* Basic Stats */ }
      { stats.map((stat) => (
        <div
            key={stat.label}
            className='rounded-xl border border-slate-200/70 bg-white p-4 text-center shadow-sm'
        >
          <NumberInfo value={stat.value} name={stat.label} loading={loading}/>
        </div>
      )) }

      { /* Years of Experience Chart */ }
      <div className='rounded-xl border border-slate-200/70 bg-white p-4 shadow-sm sm:col-span-2 xl:col-span-2'>
        <div className='mb-3 flex items-center justify-between'>
          <h3 className='text-sm font-semibold text-slate-700'>
            Experience Distribution
          </h3>
          <span className='rounded-full bg-emerald-50 px-2 py-1 text-[11px] font-semibold text-emerald-700'>
            { candidates.length } total
          </span>
        </div>
        <BarChart
            name='Years Experience'
            data={yAxisValues}
            // onClick={() => handleChartClick('Experience')}
            loading={loading}
        />
      </div>

      { /* Gender Stats */ }
      <div className='rounded-xl border border-slate-200/70 bg-white p-4 shadow-sm'>
        <h3 className='mb-3 text-sm font-semibold text-slate-700'>Gender</h3>
        { loading ? (
          <div className='grid grid-cols-2 gap-2 animate-pulse'>
            { [...Array(2)].map((_, idx) => (
              <div key={idx} className='h-6 rounded bg-gray-300'></div>
            )) }
          </div>
        ) : (
          <div className='grid grid-cols-2 gap-2'>
            { genderStats.map((item) => (
              <div
                  key={item.name}
                  className={`rounded-lg px-3 py-2 text-sm font-medium ${item.color}`}
              >
                { item.value } { item.name }
              </div>
            )) }
          </div>
        ) }
      </div>

      { /* Age Stats */ }
      <div className='rounded-xl border border-slate-200/70 bg-white p-4 shadow-sm'>
        <h3 className='mb-3 text-sm font-semibold text-slate-700'>Age</h3>
        { loading ? (
          <div className='grid grid-cols-2 gap-2 animate-pulse'>
            { [...Array(4)].map((_, idx) => (
              <div key={idx} className='flex items-center gap-2'>
                <div className='w-3 h-3 rounded-full bg-gray-300'></div>
                <div className='h-4 w-20 rounded bg-gray-300'></div>
              </div>
            )) }
          </div>
        ) : (
          <div className='grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-1'>
            { ageRange.map((item) => (
              <div
                  key={item.name}
                  className='flex items-center gap-2 text-sm text-gray-700'
              >
                <div className={`w-3 h-3 rounded-full ${item.color}`}></div>
                <span>
                  { item.name }, { item.value || 0 }
                </span>
              </div>
            )) }
          </div>
        ) }
      </div>
    </div>
  );
};
